/** 对客报价编辑期纯函数；服务端仍是保存与定稿的金额事实源。 */

import type {
  CustomerQuoteAllocationResult,
  CustomerQuoteLine,
  CustomerQuotePricingGroup,
  CustomerQuoteRoleSummary,
  CustomerQuoteSnapshot,
  CustomerQuoteStandardFinancialSummary,
  CustomerQuoteSummary,
  CustomerQuoteTemplateType,
} from "../types";

const feeCategoryOrder: CustomerQuoteLine["feeCategory"][] = [
  "big_transport",
  "bus",
  "stay",
  "meal",
  "ticket",
  "enterprise",
  "intangible",
  "guide",
  "service_fee",
  "tax",
  "misc",
  "teacher",
  "manual_adjustment",
];

export const customerQuoteFeeCategoryLabels: Record<CustomerQuoteLine["feeCategory"], string> = {
  big_transport: "大交通",
  bus: "大巴车",
  stay: "住宿",
  meal: "餐饮",
  ticket: "门票与考察",
  enterprise: "企业参访",
  intangible: "研学活动",
  guide: "导游带队",
  service_fee: "服务保障",
  tax: "税费",
  misc: "其他",
  teacher: "老师费用",
  manual_adjustment: "报价调整",
};

export type CustomerQuoteLineGroup = {
  key: string;
  groupName: string;
  feeCategory: CustomerQuoteLine["feeCategory"];
  financeTreatment: CustomerQuoteLine["financeTreatment"] | "mixed";
  costAmount: string;
  quoteAmount: string;
  profitAmount: string;
  displayMode: CustomerQuoteLine["displayMode"] | "mixed";
  lineNos: number[];
};

const moneyScale = 2;
const moneyMaxDigits = 18;
const moneyMaxValue = 10n ** BigInt(moneyMaxDigits) - 1n;
const quantityScale = 4;
const outputQuantityScale = 2;
// DECIMAL(18,2) 的完整整数分域最多需要 61 次二分，预留 64 轮。
const roleUnitPriceSolveMaxRounds = 64;

/** 把十进制字符串半上量化为指定刻度的整数。 */
function fixedValue(value: string | null | undefined, scale: number): bigint {
  const match = /^([+-]?)(\d+)(?:\.(\d*))?$/.exec((value ?? "0").trim());
  if (!match) return 0n;
  const factor = 10n ** BigInt(scale);
  const fraction = match[3] ?? "";
  const kept = fraction.slice(0, scale).padEnd(scale, "0");
  let result = BigInt(match[2]) * factor + BigInt(kept || "0");
  if (fraction.length > scale && fraction[scale] >= "5") result += 1n;
  return match[1] === "-" ? -result : result;
}

/** 格式化定点整数，避免经过 JavaScript Number。 */
function formatFixed(value: bigint, scale: number): string {
  const factor = 10n ** BigInt(scale);
  const absolute = value < 0n ? -value : value;
  const sign = value < 0n ? "-" : "";
  return `${sign}${absolute / factor}.${String(absolute % factor).padStart(scale, "0")}`;
}

/** BigInt 有理数按半上规则取整，负数中点远离零。 */
function divideHalfUp(numerator: bigint, denominator: bigint): bigint {
  if (denominator === 0n) throw new Error("定点除数不能为 0");
  const negative = (numerator < 0n) !== (denominator < 0n);
  const absoluteNumerator = numerator < 0n ? -numerator : numerator;
  const absoluteDenominator = denominator < 0n ? -denominator : denominator;
  let quotient = absoluteNumerator / absoluteDenominator;
  if ((absoluteNumerator % absoluteDenominator) * 2n >= absoluteDenominator) quotient += 1n;
  return negative ? -quotient : quotient;
}

/** 金额字符串转分。 */
function moneyValue(value: string | null | undefined): bigint {
  return fixedValue(value, moneyScale);
}

/** 数量字符串转万分之一单位，兼容来源数量最多四位小数。 */
function quantityValue(value: string | null | undefined): bigint {
  return fixedValue(value, quantityScale);
}

/** 分格式化为两位金额。 */
function money(value: bigint): string {
  return formatFixed(value, moneyScale);
}

/** 判断派生金额是否仍可落入 DECIMAL(18,2)。 */
function isCustomerQuoteMoneyInRange(value: bigint): boolean {
  const absolute = value < 0n ? -value : value;
  return absolute <= moneyMaxValue;
}

/** 将营业税报价归一为已接受的核价税费，销售加价不再生成额外税差。 */
export function recalculateCustomerQuoteTax(
  snapshot: CustomerQuoteSnapshot,
): CustomerQuoteSnapshot {
  const taxIndexes = snapshot.lines
    .map((line, index) => ({ line, index }))
    .filter(({ line }) => line.lineOrigin === "budget" && line.feeCategory === "tax")
    .sort((left, right) => {
      if (left.line.lineNo !== right.line.lineNo) return left.line.lineNo - right.line.lineNo;
      const leftSourceId = left.line.sourceBudgetLineId ?? "";
      const rightSourceId = right.line.sourceBudgetLineId ?? "";
      if (leftSourceId !== rightSourceId) return leftSourceId < rightSourceId ? -1 : 1;
      return left.index - right.index;
    })
    .map(({ index }) => index);
  if (!taxIndexes.length) return snapshot;
  const taxAmount = taxIndexes.reduce(
    (sum, index) => sum + moneyValue(snapshot.lines[index].costAmount),
    0n,
  );
  const taxIndexSet = new Set(taxIndexes);
  return {
    ...snapshot,
    lines: snapshot.lines.map((line, index) => {
      if (!taxIndexSet.has(index)) return line;
      const amount = index === taxIndexes[0] ? taxAmount : 0n;
      return {
        ...line,
        quantity: "1",
        quoteUnitPrice: money(amount),
        quoteAmount: money(amount),
        displayMode: index === taxIndexes[0] ? "show" : "hide",
        mergeTargetGroup: null,
      };
    }),
  };
}

/** 将合法十进制金额半上量化为两位，超出 DECIMAL(18, 2) 时拒绝。 */
export function normalizeCustomerQuoteMoney(value: string): string | null {
  const trimmed = value.trim();
  if (!/^([+-]?)(\d+)(?:\.(\d*))?$/.test(trimmed)) return null;
  const normalized = money(moneyValue(trimmed));
  const unsigned = normalized.startsWith("-") ? normalized.slice(1) : normalized;
  const [integer, fraction] = unsigned.split(".");
  const integerDigits = integer.replace(/^0+(?=\d)/, "").length;
  if (integerDigits + fraction.length > moneyMaxDigits) return null;
  return normalized === "-0.00" ? "0.00" : normalized;
}

/** 仅移除十进制文本的末尾零，不改变原有精度或经过浮点数。 */
export function formatCustomerQuoteCompactDecimal(value: string | number): string {
  const text = String(value);
  return text.includes(".") ? text.replace(/0+$/, "").replace(/\.$/, "") : text;
}

/** 精确格式化人民币，保留千分位；紧凑模式去掉小数末尾零，非法或超位金额仍回退为零。 */
export function formatCustomerQuoteMoney(value: string, compact = false): string {
  const normalized = normalizeCustomerQuoteMoney(value) ?? "0.00";
  const negative = normalized.startsWith("-");
  const unsigned = negative ? normalized.slice(1) : normalized;
  const [integer, fraction] = unsigned.split(".");
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const formatted = `${negative ? "-" : ""}¥${grouped}.${fraction}`;
  return compact ? formatCustomerQuoteCompactDecimal(formatted) : formatted;
}

/** 精确比较两个金额字符串；非法值沿用编辑期零值回退口径。 */
export function compareCustomerQuoteMoney(left: string, right = "0"): number {
  const leftValue = normalizeCustomerQuoteMoney(left) ?? "0.00";
  const rightValue = normalizeCustomerQuoteMoney(right) ?? "0.00";
  const difference = moneyValue(leftValue) - moneyValue(rightValue);
  return difference === 0n ? 0 : difference > 0n ? 1 : -1;
}

/** 百分之一数量格式化为最多两位小数。 */
function quantity(value: bigint): string {
  const [whole, fraction] = formatFixed(value, outputQuantityScale).split(".");
  const trimmed = fraction.replace(/0+$/, "");
  return trimmed ? `${whole}.${trimmed}` : whole;
}

/** 两位单价乘最多四位数量，金额按半上规则量化到分。 */
function multiplyMoneyByQuantity(unitAmount: bigint, quantityAmount: bigint): bigint {
  return divideHalfUp(unitAmount * quantityAmount, 10n ** BigInt(quantityScale));
}

/** 金额除以数量，单价按分向零取整。 */
function unitPriceFromAmount(amount: bigint, quantityAmount: bigint): bigint {
  if (quantityAmount === 0n) return 0n;
  return (amount * 10n ** BigInt(quantityScale)) / quantityAmount;
}

/** 编辑器即时校验结果；保存只受结构错误阻断，定稿还受业务硬错误约束。 */
export type CustomerQuoteDraftValidation = {
  draftErrors: string[];
  finalizeErrors: string[];
};

const currentPricingRulesVersion = 3;

/** 判断当前快照中的住宿是否适用每人晚规则。 */
export function isCustomerQuotePersonNightStay(
  snapshot: CustomerQuoteSnapshot,
  line: CustomerQuoteLine,
): boolean {
  return line.feeCategory === "stay"
    && (
      (snapshot.pricingRulesVersion ?? 1) < currentPricingRulesVersion
      || line.unit.trim() === "人晚"
    );
}

/** 以精确金额计算报价相对核价成本的加价百分比；零成本无可用比例。 */
export function customerQuoteMarkupRatePercent(
  line: Pick<CustomerQuoteLine, "costAmount" | "quoteAmount">,
): string | null {
  const cost = moneyValue(line.costAmount);
  if (cost === 0n) return null;
  const percentHundredths = divideHalfUp((moneyValue(line.quoteAmount) - cost) * 10000n, cost);
  return `${formatFixed(percentHundredths, 2)}%`;
}

/** 将四位小数加价率精确格式化为两位百分比，不经过 JavaScript Number。 */
export function formatCustomerQuoteMarginRatePercent(rate: string): string {
  return `${formatFixed(fixedValue(rate, 4), 2)}%`;
}

/** 判断用车手工报价是否超过自动报价的成本 30% 上限。 */
export function exceedsCustomerQuoteBusAutoMarkupCap(
  line: Pick<CustomerQuoteLine, "costAmount" | "quoteAmount">,
): boolean {
  const cost = moneyValue(line.costAmount);
  return cost > 0n && moneyValue(line.quoteAmount) - cost > (cost * 30n) / 100n;
}

/** 判断是否存在需要销售明确确认的高住宿加价。 */
export function hasHighCustomerQuoteStayMarkup(snapshot: CustomerQuoteSnapshot): boolean {
  if ((snapshot.pricingRulesVersion ?? 1) < 2) return false;
  return snapshot.lines.some((line) => (
    line.feeCategory === "stay"
    && isCustomerQuotePersonNightStay(snapshot, line)
    && moneyValue(line.quoteUnitPrice) - moneyValue(line.costUnitPrice) >= 1500n
  ));
}

/** 与后端定稿规则保持同口径，提前给销售可执行的行级反馈。 */
export function validateCustomerQuoteDraft(
  snapshot: CustomerQuoteSnapshot,
): CustomerQuoteDraftValidation {
  snapshot = recalculateCustomerQuoteTax(snapshot);
  const draftErrors: string[] = [];
  const finalizeErrors: string[] = [];
  if (!snapshot.lines.length) finalizeErrors.push("至少需要一条报价明细");

  for (const line of snapshot.lines) {
    const name = line.customerName.trim() || `第 ${line.lineNo} 行`;
    if (!line.customerName.trim()) draftErrors.push(`${name}：客户名称不能为空`);
    if (!line.unit.trim()) draftErrors.push(`${name}：计价单位不能为空`);
    if (line.lineOrigin === "manual" && !line.adjustmentReason.trim()) {
      draftErrors.push(`${name}：报价专属行必须填写调整原因`);
    }
    if (line.financeTreatment === "agent_collection" && !line.agentCollectionCategory) {
      finalizeErrors.push(`${name}：代收代付必须选择代收类别`);
    }
    if (line.financeTreatment === "agent_collection" && (
      moneyValue(line.quoteAmount) !== moneyValue(line.costAmount)
      || moneyValue(line.quoteUnitPrice) !== moneyValue(line.costUnitPrice)
    )) {
      finalizeErrors.push(`${name}：代收代付加价必须为 0，报价必须等于核价成本`);
    }
    const isBudgetTaxLine = line.lineOrigin === "budget" && line.feeCategory === "tax";
    if (
      line.profitPolicy === "none"
      && !isBudgetTaxLine
      && moneyValue(line.quoteAmount) > moneyValue(line.costAmount)
    ) {
      finalizeErrors.push(`${name}：不允许加价的费用不能高于成本`);
    }
    if (
      quoteAmountWithSourceRemainder(line)
      !== moneyValue(line.quoteAmount)
    ) {
      finalizeErrors.push(`${name}：报价金额与数量 × 单价不一致`);
    }
  }
  if (hasHighCustomerQuoteStayMarkup(snapshot) && !snapshot.stayHighMarkupConfirmed) {
    finalizeErrors.push("住宿每人晚加价达到 ¥15，需人工确认后才能定稿");
  }
  const summary = calculateCustomerQuoteSummary(snapshot);
  const summaryAmounts = [
    summary.customerPaymentTotalAmount,
    summary.agentCollectionTotalAmount,
    summary.contractReceivableAmount,
    summary.internalCostTotalAmount,
    summary.expectedGrossProfitAmount,
  ];
  if (snapshot.lines.some((line) => !isCustomerQuoteMoneyInRange(moneyValue(line.quoteAmount)))) {
    draftErrors.push("报价明细金额超出系统可保存范围");
  }
  if (summaryAmounts.some((amount) => !isCustomerQuoteMoneyInRange(moneyValue(amount)))) {
    draftErrors.push("报价汇总金额超出系统可保存范围");
  }
  if (
    moneyValue(summary.customerPaymentTotalAmount) < 0n
    || moneyValue(summary.contractReceivableAmount) < 0n
  ) {
    finalizeErrors.push("客户支付总额和合同应收不能为负数");
  }
  return { draftErrors, finalizeErrors };
}

/** 用整数分计算成本加价率，和报价汇总保持四位小数及零成本口径一致。 */
export function calculateCustomerQuoteMarginRate(profit: bigint, cost: bigint): string {
  return cost === 0n ? "0.0000" : formatFixed(divideHalfUp(profit * 10_000n, cost), 4);
}

/** 按固定恒等式计算编辑期内部汇总，显示三态不参与金额。 */
export function calculateCustomerQuoteSummary(
  snapshot: CustomerQuoteSnapshot,
): CustomerQuoteSummary {
  snapshot = recalculateCustomerQuoteTax(snapshot);
  const payment = snapshot.lines.reduce((sum, line) => sum + moneyValue(line.quoteAmount), 0n);
  const agent = snapshot.lines.reduce(
    (sum, line) => sum + (
      line.financeTreatment === "agent_collection" ? moneyValue(line.quoteAmount) : 0n
    ),
    0n,
  );
  const contract = payment - agent;
  const internalCost = snapshot.lines.reduce(
    (sum, line) => sum + moneyValue(line.costAmount),
    0n,
  );
  const grossProfit = payment - internalCost;
  return {
    customerPaymentTotalAmount: money(payment),
    agentCollectionTotalAmount: money(agent),
    contractReceivableAmount: money(contract),
    internalCostTotalAmount: money(internalCost),
    expectedGrossProfitAmount: money(grossProfit),
    expectedMarginRate: calculateCustomerQuoteMarginRate(grossProfit, internalCost),
  };
}

/** 分解报价保存的来源快照成本；来源金额缺失时保留未知，不推算实时核价。 */
export function calculateCustomerQuoteCostBreakdown(snapshot: CustomerQuoteSnapshot): {
  sourceCostTotalAmount: string | null;
  sourceTaxAmount: string | null;
  quoteTaxAmount: string;
  nonTaxCostChangeAmount: string | null;
  quoteCostTotalAmount: string;
} {
  const current = recalculateCustomerQuoteTax(snapshot);
  let sourceCostTotal = 0n;
  let sourceTax = 0n;
  let quoteTax = 0n;
  let currentNonTaxCost = 0n;
  let sourceCostMissing = false;
  let sourceTaxMissing = false;
  for (const line of current.lines) {
    const isBudgetLine = line.lineOrigin === "budget";
    const isSourceMissing = isBudgetLine && line.sourceCostAmount == null;
    sourceCostMissing ||= isSourceMissing;
    const sourceCost = isBudgetLine ? moneyValue(line.sourceCostAmount) : 0n;
    sourceCostTotal += sourceCost;
    if (line.feeCategory === "tax") {
      sourceTaxMissing ||= isSourceMissing;
      sourceTax += sourceCost;
      quoteTax += moneyValue(line.quoteAmount);
    } else {
      currentNonTaxCost += moneyValue(line.costAmount);
    }
  }
  return {
    sourceCostTotalAmount: sourceCostMissing ? null : money(sourceCostTotal),
    sourceTaxAmount: sourceTaxMissing ? null : money(sourceTax),
    quoteTaxAmount: money(quoteTax),
    nonTaxCostChangeAmount: sourceCostMissing
      ? null
      : money(currentNonTaxCost - (sourceCostTotal - sourceTax)),
    quoteCostTotalAmount: calculateCustomerQuoteSummary(current).internalCostTotalAmount,
  };
}

/** 按后端人均试算结果组成标准报价财务汇总，并加回当前快照代收代付。 */
export function calculateCustomerQuoteStandardFinancialSummary(
  snapshot: CustomerQuoteSnapshot,
  allocation: CustomerQuoteAllocationResult,
): CustomerQuoteStandardFinancialSummary {
  const currentSummary = calculateCustomerQuoteSummary(snapshot);
  const allocatedContractTotal =
    moneyValue(allocation.studentUnitPrice) * BigInt(snapshot.studentCount)
    + moneyValue(allocation.teacherUnitPrice) * BigInt(snapshot.teacherCount)
    + moneyValue(allocation.unallocatedAmount);
  const agentCollectionTotal = moneyValue(calculateCustomerQuoteAgentPresentation(snapshot).totalAmount);
  const customerPaymentTotal = allocatedContractTotal + agentCollectionTotal;
  const internalCostTotal = moneyValue(currentSummary.internalCostTotalAmount);
  const expectedGrossProfit = customerPaymentTotal - internalCostTotal;

  return {
    allocatedContractTotalAmount: money(allocatedContractTotal),
    agentCollectionTotalAmount: money(agentCollectionTotal),
    customerPaymentTotalAmount: money(customerPaymentTotal),
    internalCostTotalAmount: money(internalCostTotal),
    expectedGrossProfitAmount: money(expectedGrossProfit),
    expectedMarginRate: calculateCustomerQuoteMarginRate(expectedGrossProfit, internalCostTotal),
  };
}

/** 毛利仅在摘要按四舍五入显示整元，精确金额仍用于计算与明细。 */
export function formatCustomerQuoteWholeYuan(value: string): string {
  return formatCustomerQuoteMoney(money(divideHalfUp(moneyValue(value), 100n) * 100n), true);
}

/** 按指定金额精度跨大项归集，最大余数补齐；冻结金额允许保留分位。 */
function distributeQuoteAmount(total: string, weights: bigint[], precision = 100n): bigint[] {
  if (!weights.length) return [];
  let denominator = weights.reduce((sum, value) => sum + value, 0n);
  if (denominator === 0n) {
    weights = weights.map((value) => value < 0n ? -value : value);
    denominator = weights.reduce((sum, value) => sum + value, 0n);
  }
  if (denominator === 0n) { weights = weights.map(() => 1n); denominator = BigInt(weights.length); }
  const sign = denominator < 0n ? -1n : 1n;
  denominator *= sign;
  const yuan = moneyValue(total) / precision;
  const parts = weights.map((weight, index) => {
    const numerator = yuan * weight * sign;
    const remainder = ((numerator % denominator) + denominator) % denominator;
    return { index, value: (numerator - remainder) / denominator, remainder };
  });
  let remaining = yuan - parts.reduce((sum, part) => sum + part.value, 0n);
  for (const part of [...parts].sort((a, b) => a.remainder === b.remainder ? a.index - b.index : a.remainder > b.remainder ? -1 : 1)) {
    if (remaining-- > 0n) part.value += 1n;
  }
  return parts.map((part) => part.value * precision);
}

/** 只生成页面整元口径；不改草稿、分摊接口原结果或成交冻结金额。 */
export function buildCustomerQuoteRoundedPresentation(
  snapshot: CustomerQuoteSnapshot,
  source: CustomerQuoteAllocationResult,
  { preserveFrozenAmounts = false }: { preserveFrozenAmounts?: boolean } = {},
) {
  const ceilYuan = (value: string) => {
    if (preserveFrozenAmounts) return value;
    const cents = moneyValue(value);
    return money((cents / 100n + (cents % 100n > 0n ? 1n : 0n)) * 100n);
  };
  const items = source.groupBreakdown ?? source.breakdown.map((item) => ({
    ...item, sourceLineIds: item.sourceLines.map((line) => line.lineId),
  }));
  const extra = items.reduce((sum, item) => sum + moneyValue(ceilYuan(item.roundedAmount)) - moneyValue(item.roundedAmount), 0n);
  const allocation = {
    ...source,
    unallocatedAmount: money(moneyValue(source.unallocatedAmount) + extra),
    upliftTotalAmount: money(moneyValue(source.upliftTotalAmount) + extra),
    breakdown: source.breakdown.map((item) => ({ ...item, roundedAmount: ceilYuan(item.roundedAmount) })),
  };
  const financialSummary = calculateCustomerQuoteStandardFinancialSummary(snapshot, allocation);
  const groups = groupCustomerQuoteLines(snapshot).map((group) => ({ ...group, studentUnitPrice: "0.00", teacherUnitPrice: "0.00" }));
  const byId = new Map(snapshot.lines.filter((line) => line.customerQuoteLineId).map((line) => [line.customerQuoteLineId, line]));
  const amounts = groups.map((group) => moneyValue(calculateCustomerQuoteAgentPresentation({
    ...snapshot, lines: snapshot.lines.filter((line) => group.lineNos.includes(line.lineNo)),
  }).totalAmount));
  const units = groups.map(() => 0n);
  const teacherUnits = groups.map(() => 0n);
  const seen = new Set<string>();
  let complete = byId.size === snapshot.lines.filter((line) => line.customerQuoteLineId).length;
  for (const item of items) {
    const weights = groups.map(() => 0n);
    const studentWeights = groups.map(() => 0n);
    const teacherWeights = groups.map(() => 0n);
    const indices = new Set<number>();
    for (const id of item.sourceLineIds) {
      const line = id ? byId.get(id) : undefined;
      if (!line || !id || seen.has(id) || line.financeTreatment !== "contract_receivable") { complete = false; continue; }
      seen.add(id);
      const index = groups.findIndex((group) => group.lineNos.includes(line.lineNo));
      if (index < 0) { complete = false; continue; }
      indices.add(index);
      const amount = moneyValue(line.quoteAmount);
      weights[index] += amount;
      const studentBears = line.allocationMode === "student" || (line.allocationMode === "teacher" && line.teacherPaymentPolicy === "student_share") || (line.allocationMode === "all" && !snapshot.sourceTeacherPays);
      const people = BigInt(snapshot.studentCount + snapshot.teacherCount || 1);
      studentWeights[index] += studentBears ? amount * people : line.allocationMode === "all" ? amount * BigInt(snapshot.studentCount) : 0n;
      teacherWeights[index] += line.allocationMode === "teacher" && line.teacherPaymentPolicy !== "student_share"
        ? amount * people : line.allocationMode === "all" && snapshot.sourceTeacherPays ? amount * BigInt(snapshot.teacherCount) : 0n;
    }
    if (!indices.size) { complete = false; continue; }
    const ordered = [...indices].sort((a, b) => a - b);
    const precision = preserveFrozenAmounts ? 1n : 100n;
    const prices = distributeQuoteAmount(ceilYuan(item.roundedAmount), ordered.map((index) => weights[index]), precision);
    const perStudent = distributeQuoteAmount(item.studentUnitPrice, ordered.map((index) => studentWeights[index]), precision);
    const perTeacher = distributeQuoteAmount(item.teacherUnitPrice, ordered.map((index) => teacherWeights[index]), precision);
    ordered.forEach((index, position) => { amounts[index] += prices[position]; units[index] += perStudent[position]; teacherUnits[index] += perTeacher[position]; });
  }
  complete &&= snapshot.lines.filter((line) => line.financeTreatment === "contract_receivable")
    .every((line) => Boolean(line.customerQuoteLineId && seen.has(line.customerQuoteLineId)));
  complete &&= amounts.reduce((sum, amount) => sum + amount, 0n) === moneyValue(financialSummary.customerPaymentTotalAmount)
    && units.reduce((sum, amount) => sum + amount, 0n) === moneyValue(allocation.studentUnitPrice)
    && teacherUnits.reduce((sum, amount) => sum + amount, 0n) === moneyValue(allocation.teacherUnitPrice);
  return {
    allocation,
    financialSummary,
    groups: complete ? groups.map((group, index) => ({ ...group, quoteAmount: money(amounts[index]), studentUnitPrice: money(units[index]), teacherUnitPrice: money(teacherUnits[index]), profitAmount: money(amounts[index] - moneyValue(group.costAmount)) })) : null,
  };
}

/** 代收按实际金额显示分位人均，不用人均反推总额或增加取整补差。 */
export function calculateCustomerQuoteAgentPresentation(snapshot: CustomerQuoteSnapshot) {
  const lines = snapshot.lines.filter((line) => line.financeTreatment === "agent_collection");
  const roles = calculateCustomerQuoteRoleSummary({ ...snapshot, lines }, "quoteAmount", true);
  return {
    studentUnitPrice: snapshot.studentCount > 0 ? roles.studentUnitPrice : null,
    teacherUnitPrice: roles.teacherUnitPrice,
    totalAmount: money(lines.reduce((sum, line) => sum + moneyValue(line.quoteAmount), 0n)),
  };
}

/** 计算代收学生人均，除不尽时按分展示，不改变原始总额。 */
export function calculateCustomerQuoteAgentPerStudent(snapshot: CustomerQuoteSnapshot): string | null {
  return calculateCustomerQuoteAgentPresentation(snapshot).studentUnitPrice;
}

/** 用整数分合计合同与代收人均，保持未知人数状态。 */
export function addCustomerQuoteAgentPerStudent(contractUnit: string, agentUnit: string | null): string | null {
  return agentUnit === null ? null : money(moneyValue(contractUnit) + moneyValue(agentUnit));
}

/** 汇总顶部学生核价人均与对客人均毛利。 */
export function calculateCustomerQuoteHeaderMetrics(snapshot: CustomerQuoteSnapshot, studentUnitPrice: string) {
  if (snapshot.studentCount <= 0) return { customerPerStudent: null, costPerStudent: null, differencePerStudent: null };
  const customerPerStudent = addCustomerQuoteAgentPerStudent(studentUnitPrice, calculateCustomerQuoteAgentPerStudent(snapshot))!;
  const costPerStudent = calculateCustomerQuoteRoleSummary(snapshot, "costAmount", true).studentUnitPrice;
  return { customerPerStudent, costPerStudent, differencePerStudent: money(moneyValue(customerPerStudent) - moneyValue(costPerStudent)) };
}

/** 按承担规则分配角色单价，含代收投影仅用于人均核对。 */
export function calculateCustomerQuoteRoleSummary(
  snapshot: CustomerQuoteSnapshot,
  amountField: "quoteAmount" | "costAmount" = "quoteAmount",
  includeAgentCollection = false,
): CustomerQuoteRoleSummary {
  snapshot = recalculateCustomerQuoteTax(snapshot);
  const totalPeople = snapshot.studentCount + snapshot.teacherCount;
  const denominator = BigInt(totalPeople || 1);
  let studentAmount = 0n;
  let teacherAmount = 0n;
  let fixedAmount = 0n;
  for (const line of snapshot.lines) {
    if (line.financeTreatment !== "contract_receivable" && !includeAgentCollection) continue;
    // 旧固定大交通只在含代收的人均投影中分摊，不改真实合同及明细。
    const allocationMode = includeAgentCollection && line.financeTreatment === "agent_collection"
      && line.allocationMode === "fixed" && line.feeCategory === "big_transport" ? "all" : line.allocationMode;
    const amount = moneyValue(line[amountField]);
    if (allocationMode === "student") {
      studentAmount += amount * denominator;
    } else if (allocationMode === "teacher") {
      if (line.teacherPaymentPolicy === "student_share") studentAmount += amount * denominator;
      else teacherAmount += amount * denominator;
    } else if (allocationMode === "all") {
      if (!snapshot.sourceTeacherPays) {
        studentAmount += amount * denominator;
      } else if (totalPeople > 0) {
        studentAmount += amount * BigInt(snapshot.studentCount);
        teacherAmount += amount * BigInt(snapshot.teacherCount);
      } else {
        fixedAmount += amount * denominator;
      }
    } else {
      fixedAmount += amount * denominator;
    }
  }
  const roleUnit = (amount: bigint, people: number) => {
    const divisor = denominator * BigInt(people);
    return divideHalfUp(amount, divisor);
  };
  const studentUnit = snapshot.studentCount
    ? roleUnit(studentAmount, snapshot.studentCount)
    : 0n;
  const teacherUnit = snapshot.teacherCount
    ? roleUnit(teacherAmount, snapshot.teacherCount)
    : 0n;
  fixedAmount += studentAmount - studentUnit * BigInt(snapshot.studentCount) * denominator;
  fixedAmount += teacherAmount - teacherUnit * BigInt(snapshot.teacherCount) * denominator;
  return {
    studentUnitPrice: money(studentUnit),
    teacherUnitPrice: money(teacherUnit),
    fixedContractAmount: money(divideHalfUp(fixedAmount, denominator)),
  };
}

/** 按固定报价大项汇总底层费用项，可切换代收的费用不按财务口径拆组。 */
export function groupCustomerQuoteLines(snapshot: CustomerQuoteSnapshot): CustomerQuoteLineGroup[] {
  snapshot = recalculateCustomerQuoteTax(snapshot);
  const grouped = new Map<string, {
    feeCategory: CustomerQuoteLine["feeCategory"];
    financeTreatments: Set<CustomerQuoteLine["financeTreatment"]>;
    costAmount: bigint;
    quoteAmount: bigint;
    profitAmount: bigint;
    displayModes: Set<CustomerQuoteLine["displayMode"]>;
    lineNos: number[];
  }>();
  for (const line of snapshot.lines) {
    const key = ["big_transport", "ticket"].includes(line.feeCategory)
      ? line.feeCategory
      : `${line.feeCategory}:${line.financeTreatment}`;
    const group = grouped.get(key) ?? {
      feeCategory: line.feeCategory,
      financeTreatments: new Set<CustomerQuoteLine["financeTreatment"]>(),
      costAmount: 0n,
      quoteAmount: 0n,
      profitAmount: 0n,
      displayModes: new Set<CustomerQuoteLine["displayMode"]>(),
      lineNos: [],
    };
    group.financeTreatments.add(line.financeTreatment);
    const costAmount = moneyValue(line.costAmount);
    group.costAmount += costAmount;
    group.quoteAmount += moneyValue(line.quoteAmount);
    group.profitAmount += moneyValue(line.quoteAmount) - costAmount;
    group.displayModes.add(line.feeCategory === "tax" ? "show" : line.displayMode);
    group.lineNos.push(line.lineNo);
    grouped.set(key, group);
  }
  return [...grouped.entries()]
    .sort(([, left], [, right]) => {
      const category = feeCategoryOrder.indexOf(left.feeCategory) - feeCategoryOrder.indexOf(right.feeCategory);
      if (category !== 0) return category;
      return left.financeTreatments.has("agent_collection") ? 1 : -1;
    })
    .map(([key, group]) => {
      const financeTreatment = group.financeTreatments.size === 1
        ? [...group.financeTreatments][0]
        : "mixed";
      return {
        key,
        groupName: `${group.feeCategory === "tax" ? "营业税" : customerQuoteFeeCategoryLabels[group.feeCategory]}${
          !["big_transport", "ticket"].includes(group.feeCategory) && financeTreatment === "agent_collection"
            ? "（代收代付）"
            : ""
        }`,
        feeCategory: group.feeCategory,
        financeTreatment,
        costAmount: money(group.costAmount),
        quoteAmount: money(group.quoteAmount),
        profitAmount: money(group.profitAmount),
        displayMode: group.displayModes.size === 1 ? [...group.displayModes][0] : "mixed",
        lineNos: group.lineNos,
      };
    });
}

/** 费用分类可能来自旧快照，真实资源类型与分类必须同时命中大交通。 */
export function isCustomerQuoteBigTransportLine(
  line: Pick<CustomerQuoteLine, "resourceType" | "feeCategory">,
): boolean {
  return line.resourceType === "大交通" && line.feeCategory === "big_transport";
}

/** 仅核价的大交通及门票与考察允许手动切换代收。 */
export function canToggleCustomerQuoteAgentCollection(line: CustomerQuoteLine): boolean {
  return line.lineOrigin === "budget" && (isCustomerQuoteBigTransportLine(line)
    || (line.resourceType === "考察点" && line.feeCategory === "ticket"));
}

/** 切换收款口径，开启代收时清零加价并同步类别，关闭后不恢复旧加价。 */
export function applyCustomerQuoteAgentCollection(
  line: CustomerQuoteLine,
  checked: boolean,
): CustomerQuoteLine {
  if (!canToggleCustomerQuoteAgentCollection(line)) return line;
  const sourceText = [
    line.sourceItemName,
    line.customerName,
    line.sourceResourceEntryType,
  ].filter(Boolean).join(" ");
  const category = line.feeCategory === "ticket" ? "ticket"
    : /飞机|机票|航班/.test(sourceText) ? "flight_ticket" : "train_ticket";
  return {
    ...line,
    financeTreatment: checked ? "agent_collection" : "contract_receivable",
    agentCollectionCategory: checked ? category : null,
    ...(checked ? { quoteUnitPrice: line.costUnitPrice, quoteAmount: line.costAmount } : {}),
  };
}

const pricingGroupCategories = new Set<CustomerQuoteLine["feeCategory"]>([
  "stay",
  "ticket",
  "enterprise",
  "intangible",
]);
const pointPricingCategories = new Set<CustomerQuoteLine["feeCategory"]>([
  "ticket",
  "enterprise",
  "intangible",
]);
const customerRole = /^(?:学生|老师|教师|院长|导游|领队|司机)$/;
const customerRolePrefix = /^(?:学生|老师|教师|院长|导游|领队|司机)[\s:：\-—_/·–]+/;
const customerRoleSuffix = /\s*[（(](?:学生|老师|教师|院长|导游|领队|司机)[）)]\s*$/;
const customerRoleBareSuffix = /[\s:：\-—_/·–]+(?:学生|老师|教师|院长|导游|领队|司机)\s*$/;
const legacyV3StayRoleBareSuffix = /(?:学生|老师|教师|院长|导游|领队|司机)\s*$/;
const teacherRoles = new Set(["teacher", "老师", "教师", "院长"]);

/** 仅去除费用名称中具有明确结构边界的角色标记。 */
function normalizeCustomerQuoteItemName(customerName: string): string {
  let normalized = customerName.trim().replace(/\s+/g, " ");
  if (customerRole.test(normalized)) return "";
  normalized = normalized
    .replace(customerRolePrefix, "")
    .replace(customerRoleSuffix, "")
    .replace(customerRoleBareSuffix, "")
    .trim();
  return normalized.replace(/\s+/g, " ");
}

/** 仅兼容 V3 住宿连续裸角色后缀，不得用于通用名称归一。 */
function normalizeLegacyV3StayPricingName(value: string): string {
  return value.replace(legacyV3StayRoleBareSuffix, "").trim().replace(/\s+/g, " ");
}

/** 生成与后端一致的同名费用稳定展示名称。 */
export function normalizeCustomerQuotePricingName(
  line: CustomerQuoteLine,
  pricingRulesVersion = 1,
): string {
  let normalized = normalizeCustomerQuoteItemName(line.customerName);
  if (
    line.feeCategory === "stay"
    && pricingRulesVersion >= currentPricingRulesVersion
  ) {
    normalized = normalizeLegacyV3StayPricingName(normalized);
  }
  return normalized || customerQuoteFeeCategoryLabels[line.feeCategory];
}

/** 只读展示大巴来源的区域、服务类型和天次，不从车型或费用名称推测。 */
export function customerQuoteBusScope(line: CustomerQuoteLine): { region: string; scope: string } | null {
  if (line.feeCategory !== "bus") return null;
  const raw = line.sourceEntrySnapshot?.context;
  const context = raw && typeof raw === "object" && !Array.isArray(raw)
    ? raw as Record<string, unknown> : {};
  const region = typeof context.regionName === "string" ? context.regionName.trim() : "";
  const service = context.serviceType === "local_charter" ? "当地用车"
    : context.serviceType === "pickup" ? "单接"
      : context.serviceType === "dropoff" ? "单送" : "类型待确认";
  const days = context.dayNumbers;
  let range = "天次待确认";
  if (Array.isArray(days) && days.length && days.every((day, index) => (
    typeof day === "number" && Number.isSafeInteger(day) && day > 0
    && (index === 0 || day > days[index - 1])
  ))) {
    range = days.length === 1 ? `第${days[0]}天`
      : days.every((day, index) => index === 0 || day === days[index - 1] + 1)
        ? `第${days[0]}至${days.at(-1)}天` : `仅第${days.join("、")}天`;
  }
  return { region: region || "区域待确认", scope: `${range} · ${service}` };
}

/** 门票/校企/非遗考察点展示名；用于拆开同名加价组。 */
export function customerQuotePointLabel(line: CustomerQuoteLine): string {
  if (!pointPricingCategories.has(line.feeCategory)) return "";
  return (line.sourceSpuName ?? "").trim();
}

/** 与后端一致的考察点分组令牌：优先 SPU ID，其次考察点名称。 */
export function customerQuotePricingPointToken(line: CustomerQuoteLine): string {
  if (!pointPricingCategories.has(line.feeCategory)) return "";
  const spuId = (line.sourceSpuId ?? "").trim();
  if (spuId) return `spu:${spuId}`;
  const spuName = customerQuotePointLabel(line);
  if (spuName) return `spu_name:${spuName}`;
  return "";
}

/** 考察点列合并键：优先同名展示合并，避免同名不同 SPU ID 拆成多格。 */
function customerQuotePointDisplayToken(line: CustomerQuoteLine): string {
  if (!pointPricingCategories.has(line.feeCategory)) return "";
  const spuName = customerQuotePointLabel(line);
  if (spuName) return `spu_name:${spuName}`;
  const spuId = (line.sourceSpuId ?? "").trim();
  if (spuId) return `spu:${spuId}`;
  return "";
}

/** 按展示顺序合并连续同名考察点或住宿区域；区域仅使用已知住宿来源，不猜测费用名称。 */
export function buildCustomerQuotePointDisplayRuns(
  lines: CustomerQuoteLine[],
  location: "point" | "stay" = "point",
): Map<number, { rowSpan: number; label: string }> {
  const labelFor = (line: CustomerQuoteLine) => location === "stay"
    ? line.feeCategory === "stay" && ["dim_stay_area", "resource_stay_budget_spu"].includes(line.sourceSpuTableName ?? "")
      ? (line.sourceSpuName ?? "").trim() : ""
    : customerQuotePointLabel(line);
  const tokenFor = (line: CustomerQuoteLine) => location === "stay"
    ? labelFor(line) : customerQuotePointDisplayToken(line);
  const runs = new Map<number, { rowSpan: number; label: string }>();
  let index = 0;
  while (index < lines.length) {
    const line = lines[index];
    const token = tokenFor(line);
    const label = labelFor(line);
    if (!token) {
      runs.set(line.lineNo, { rowSpan: 1, label: label || "—" });
      index += 1;
      continue;
    }
    let end = index + 1;
    while (
      end < lines.length
      && tokenFor(lines[end]) === token
    ) {
      end += 1;
    }
    runs.set(line.lineNo, {
      rowSpan: end - index,
      label: label || "—",
    });
    index = end;
  }
  return runs;
}

/** 把明细行整理为编辑器可用的名称合并组，未命中规则的费用保持单行。 */
export function buildCustomerQuotePricingGroups(
  lines: CustomerQuoteLine[],
  pricingRulesVersion = 1,
): CustomerQuotePricingGroup[] {
  const groups = new Map<string, CustomerQuotePricingGroup>();
  for (const line of lines) {
    const grouped = pricingGroupCategories.has(line.feeCategory);
    const name = grouped
      ? normalizeCustomerQuotePricingName(line, pricingRulesVersion)
      : line.customerName;
    const role = (line.travelerRole ?? "").trim().toLowerCase();
    const stayRoles: Record<string, string> = {
      学生: "student", 老师: "teacher", 教师: "teacher", 院长: "teacher",
      导游: "guide", 领队: "guide", 司机: "driver",
    };
    const pointToken = line.feeCategory === "stay" && pricingRulesVersion >= 3
      ? stayRoles[role] ?? role
      : grouped ? customerQuotePricingPointToken(line) : "";
    const pointLabel = grouped ? customerQuotePointLabel(line) : "";
    const key = grouped
      ? `${line.feeCategory}:${line.financeTreatment}:${name}:${pointToken}`
      : `line:${line.lineNo}`;
    const factorMode: CustomerQuotePricingGroup["factorMode"] =
      line.profitPolicy === "none"
        ? "locked"
        : line.feeCategory === "ticket"
          ? "ticket_teacher_double"
          : grouped
            ? "same"
            : "single";
    const current = groups.get(key);
    if (current) {
      current.lineNos.push(line.lineNo);
      current.lines.push(line);
      continue;
    }
    groups.set(key, {
      key,
      name,
      pointLabel,
      feeCategory: line.feeCategory,
      financeTreatment: line.financeTreatment,
      lineNos: [line.lineNo],
      lines: [line],
      factorMode,
    });
  }
  return [...groups.values()].sort((left, right) => left.lineNos[0] - right.lineNos[0]);
}

/** 读取统一加价组当前的基础加价。 */
export function customerQuotePricingGroupBaseMarkup(
  group: CustomerQuotePricingGroup,
): string {
  const line = group.lines[0];
  const role = (line.travelerRole ?? "").trim().toLowerCase();
  const factor = line.feeCategory === "ticket" && teacherRoles.has(role) ? 2n : 1n;
  return money(
    (moneyValue(line.quoteUnitPrice) - moneyValue(line.costUnitPrice)) / factor,
  );
}

/** 计算大项卡片的实际加价差额，不套用财务毛利归零规则。 */
export function customerQuoteGroupMarkup(
  group: Pick<CustomerQuoteLineGroup, "costAmount" | "quoteAmount">,
): string {
  return money(moneyValue(group.quoteAmount) - moneyValue(group.costAmount));
}

/** 切换客户模板标识，保留销售手工设置的展示方式与合并目标。 */
export function applyCustomerQuoteTemplate(
  snapshot: CustomerQuoteSnapshot,
  templateType: CustomerQuoteTemplateType,
): CustomerQuoteSnapshot {
  return { ...snapshot, templateType };
}

type PeopleCounts = Pick<CustomerQuoteSnapshot, "studentCount" | "teacherCount"> &
  Partial<Pick<CustomerQuoteSnapshot, "guideCount" | "driverCount">>;

/** 取得一行在来源或当前快照中对应的人数。 */
function rolePeople(
  line: CustomerQuoteLine,
  snapshot: CustomerQuoteSnapshot,
  source: boolean,
): number {
  const student = source ? snapshot.sourceStudentCount : snapshot.studentCount;
  const teacher = source ? snapshot.sourceTeacherCount : snapshot.teacherCount;
  const guide = source ? snapshot.sourceGuideCount : snapshot.guideCount;
  const driver = source ? snapshot.sourceDriverCount : snapshot.driverCount;
  const role = (line.travelerRole ?? "").trim().toLowerCase();
  if (role === "guide" || role === "导游") return guide;
  if (role === "driver" || role === "司机") return driver;
  if (line.allocationMode === "student") return student;
  if (line.allocationMode === "teacher") return teacher;
  return student + teacher;
}

/** 把整数字符串或仅含尾随零的小数字符串还原为正整数。 */
function positiveInteger(value: string | number | null | undefined): bigint | null {
  const match = /^(\d+)(?:\.(\d*))?$/.exec(String(value ?? "").trim());
  if (!match || (match[2] ?? "").replace(/0+$/, "") !== "") return null;
  const whole = BigInt(match[1] || "0");
  return whole > 0n ? whole : null;
}

/** 人数与数量能整除时返回次数，避免把人晚乘积误当成带小数的人数。 */
function exactRepeatCount(quantityText: string, people: bigint): bigint | null {
  const total = quantityValue(quantityText);
  const divisor = people * (10n ** BigInt(quantityScale));
  if (divisor === 0n || total % divisor !== 0n) return null;
  const count = total / divisor;
  return count > 0n ? count : null;
}

/** 将随人数的人晚、人餐、人天乘积拆为整数人数与次数；不能整除的联动行仍按半上口径。 */
export function formatCustomerQuoteLineQuantity(
  line: CustomerQuoteLine,
  snapshot: CustomerQuoteSnapshot,
): string {
  const suffix = line.unit === "人晚"
    ? "晚"
    : line.unit === "人餐"
      ? "餐"
      : line.unit === "人天"
        ? "天"
        : line.unit === "人次"
          ? "次"
          : null;
  const raw = `${formatCustomerQuoteCompactDecimal(line.quantity)} ${line.unit}`;
  if (line.lineOrigin !== "budget" || suffix === null) return raw;
  const source = line.sourceEntrySnapshot;
  if (source?.inputMode === "manual_v2"
    && (source.quantityMode === "linked_people" || source.quantityMode === "fixed_people")
    && typeof source.billingCount === "number" && Number.isSafeInteger(source.billingCount) && source.billingCount > 0) {
    const factor = BigInt(source.billingCount) * 10n ** BigInt(quantityScale);
    const total = quantityValue(line.quantity);
    return total % factor === 0n ? `${total / factor}人 × ${source.billingCount}${suffix}` : raw;
  }
  const linkedPeople = line.isLinkedToPeopleCount ? rolePeople(line, snapshot, false) : 0;
  const people = line.isLinkedToPeopleCount
    ? (linkedPeople > 0 ? BigInt(linkedPeople) : null)
    : positiveInteger(line.sourceManualPeopleCount);
  if (people !== null) {
    const exact = exactRepeatCount(line.quantity, people);
    if (exact !== null) return `${people}人 × ${exact}${suffix}`;
    if (line.isLinkedToPeopleCount) {
      const count = divideHalfUp(quantityValue(line.quantity), people * 100n);
      return `${people}人 × ${quantity(count)}${suffix}`;
    }
  }
  const recorded = suffix === "晚"
    ? line.sourceNightCount
    : suffix === "天"
      ? line.sourceDayCount
      : null;
  const factor = positiveInteger(recorded);
  if (factor !== null) {
    const total = quantityValue(line.quantity);
    const scale = 10n ** BigInt(quantityScale);
    if (total % (factor * scale) === 0n) {
      const derivedPeople = total / (factor * scale);
      if (derivedPeople > 0n) return `${derivedPeople}人 × ${factor}${suffix}`;
    }
  }
  return raw;
}

/** 明细列在能拆出人数时标题为人数，其余费用仍是数量。 */
export function customerQuoteDetailQuantityHeading(
  lines: CustomerQuoteLine[],
  snapshot: CustomerQuoteSnapshot,
): string {
  if (
    lines.length > 0
    && lines.every((line) => formatCustomerQuoteLineQuantity(line, snapshot).includes("人 ×"))
  ) {
    return "人数";
  }
  return "数量";
}

/** 按明确计费次数或明细冻结人数计算联动数量；旧数据才回退到共享表头。 */
export function calculateCustomerQuoteLinkedQuantity(line: CustomerQuoteLine, snapshot: CustomerQuoteSnapshot, scale = quantityScale): string | null {
  const sourcePeople = line.sourcePeopleCount ?? rolePeople(line, snapshot, true);
  const currentPeople = rolePeople(line, snapshot, false);
  if (![sourcePeople, currentPeople].every((count) => Number.isSafeInteger(count) && count >= 0)) {
    throw new Error("报价人数必须为非负安全整数");
  }
  const entry = line.sourceEntrySnapshot;
  const billingCount = entry?.billingCount;
  let count: bigint;
  if (entry?.quantityMode === "linked_people"
    && typeof billingCount === "number" && Number.isSafeInteger(billingCount) && billingCount > 0) {
    count = BigInt(currentPeople) * BigInt(billingCount) * 10n ** BigInt(scale);
  } else if (sourcePeople === 0) {
    if (currentPeople > 0) throw new Error("来源人数为 0，需返回核价补齐后再调整");
    return null;
  } else {
    count = divideHalfUp(quantityValue(line.sourceQuantity) * BigInt(currentPeople), BigInt(sourcePeople) * 10n ** BigInt(quantityScale - scale));
  }
  return formatCustomerQuoteCompactDecimal(formatFixed(count, scale));
}

/** 修改当前报价人数，优先按明确计费次数、其次按来源比例重算，不修改 source 字段。 */
export function applyCustomerQuotePeopleCounts(
  snapshot: CustomerQuoteSnapshot,
  counts: PeopleCounts,
): CustomerQuoteSnapshot {
  const next: CustomerQuoteSnapshot = {
    ...snapshot,
    studentCount: counts.studentCount,
    teacherCount: counts.teacherCount,
    guideCount: counts.guideCount ?? snapshot.guideCount,
    driverCount: counts.driverCount ?? snapshot.driverCount,
    lines: [],
  };
  const peopleCounts = [next.studentCount, next.teacherCount, next.guideCount, next.driverCount];
  if (!peopleCounts.every((count) => Number.isSafeInteger(count) && count >= 0)) {
    throw new Error("报价人数必须为非负安全整数");
  }
  next.lines = snapshot.lines.map((line) => {
    if (line.lineOrigin === "manual") {
      if (line.adjustmentKind === "student_unit_price" || line.adjustmentKind === "teacher_unit_price") {
        const count =
          line.adjustmentKind === "student_unit_price" ? next.studentCount : next.teacherCount;
        return {
          ...line,
          quantity: quantity(BigInt(count) * 100n),
          quoteAmount: money(moneyValue(line.quoteUnitPrice) * BigInt(count)),
        };
      }
      return line;
    }
    if (!line.isLinkedToPeopleCount) return line;
    const linkedQuantity = calculateCustomerQuoteLinkedQuantity(line, next, outputQuantityScale);
    if (linkedQuantity === null) return line;
    const nextQuantity = divideHalfUp(quantityValue(linkedQuantity), 100n);
    const nextQuantityValue = nextQuantity * 100n;
    return {
      ...line,
      quantity: quantity(nextQuantity),
      costAmount: money(multiplyMoneyByQuantity(moneyValue(line.costUnitPrice), nextQuantityValue)),
      quoteAmount: money(multiplyMoneyByQuantity(moneyValue(line.quoteUnitPrice), nextQuantityValue)),
    };
  });
  return roundCustomerQuoteAutoServiceFee(next);
}

/** 自动服务保障按付费人群取整；仅在可编辑草稿的变更路径调用。 */
export function roundCustomerQuoteAutoServiceFee(snapshot: CustomerQuoteSnapshot): CustomerQuoteSnapshot {
  const people = BigInt(snapshot.studentCount + (snapshot.sourceTeacherPays ? snapshot.teacherCount : 0));
  return {
    ...snapshot,
    lines: snapshot.lines.map((line) => {
      if (line.lineOrigin !== "manual" || line.sourceBudgetLineId != null
        || line.feeCategory !== "service_fee" || moneyValue(line.costAmount) !== 0n
        || quantityValue(line.quantity) !== 10_000n || line.financeTreatment !== "contract_receivable"
        || !["自动补足目标加价率的服务保障费", "自动补足目标毛利率的服务保障费"].includes(line.adjustmentReason)) return line;
      const amount = moneyValue(line.quoteAmount);
      const divisor = people * 100n;
      const rounded = people > 0n
        ? (amount / divisor + (amount % divisor > 0n ? 1n : 0n)) * divisor : amount;
      return {
        ...line, allocationMode: "all" as const, teacherPaymentPolicy: "not_applicable" as const,
        quoteAmount: money(rounded), quoteUnitPrice: money(rounded),
      };
    }),
  };
}

/** 用唯一的零成本手工调整行表达学生或老师目标单价。 */
export function applyCustomerQuoteRoleUnitPrice(
  snapshot: CustomerQuoteSnapshot,
  role: "student" | "teacher",
  targetUnitPrice: string,
): CustomerQuoteSnapshot {
  if (role === "teacher" && !snapshot.sourceTeacherPays) {
    throw new Error("老师不单独付费时不能调整老师报价单价");
  }
  const count = role === "student" ? snapshot.studentCount : snapshot.teacherCount;
  const rawTarget = targetUnitPrice.trim();
  const rawTargetIsNegative = /^-\d+(?:\.\d*)?$/.test(rawTarget) && /[1-9]/.test(rawTarget);
  const target = moneyValue(targetUnitPrice);
  if (count <= 0 || rawTargetIsNegative || target < 0n) {
    throw new Error("角色人数必须大于 0，且报价单价不能为负数");
  }
  const adjustmentKind = `${role}_unit_price` as const;
  const remaining = snapshot.lines.filter((line) => line.adjustmentKind !== adjustmentKind);
  const base = recalculateCustomerQuoteTax({ ...snapshot, lines: remaining });
  const roleSummary = calculateCustomerQuoteRoleSummary(base);
  const baseUnit = moneyValue(
    role === "student" ? roleSummary.studentUnitPrice : roleSummary.teacherUnitPrice,
  );
  if (baseUnit === target) return base;
  const roleName = role === "student" ? "学生" : "老师";
  const lineNo = Math.max(0, ...remaining.map((line) => line.lineNo)) + 1;
  const makeCandidate = (deltaUnit: bigint): CustomerQuoteSnapshot => {
    if (deltaUnit === 0n) return base;
    const adjustment: CustomerQuoteLine = {
      lineOrigin: "manual",
      sourceBudgetLineId: null,
      lineNo,
      resourceType: "杂费",
      feeCategory: "manual_adjustment",
      travelerRole: role,
      sourceQuantity: null,
      quantity: quantity(BigInt(count) * 100n),
      unit: "人",
      costUnitPrice: "0",
      sourceCostAmount: null,
      costAmount: "0",
      quoteUnitPrice: money(deltaUnit),
      quoteAmount: money(deltaUnit * BigInt(count)),
      customerName: `${roleName}团费调整`,
      customerDesc: "",
      profitPolicy: "allowed",
      displayMode: "hide",
      mergeTargetGroup: "综合服务",
      allocationMode: role,
      isLinkedToPeopleCount: false,
      peopleMode: "manual",
      teacherPaymentPolicy: "not_applicable",
      financeTreatment: "contract_receivable",
      agentCollectionCategory: null,
      agentCollectionSuggested: false,
      isCostLocked: true,
      adjustmentKind,
      adjustmentReason: `${roleName}报价单价调整`,
      remark: "",
    };
    return recalculateCustomerQuoteTax({ ...base, lines: [...remaining, adjustment] });
  };

  // 核价税费固定参与原单价分摊；只调整角色收费，并在合法金额分域内精确求解。
  const maxDeltaUnit = moneyMaxValue / BigInt(count);
  let lowerDeltaUnit = -maxDeltaUnit;
  let upperDeltaUnit = maxDeltaUnit;
  for (
    let round = 0;
    round < roleUnitPriceSolveMaxRounds && lowerDeltaUnit <= upperDeltaUnit;
    round += 1
  ) {
    const deltaUnit = lowerDeltaUnit + (upperDeltaUnit - lowerDeltaUnit) / 2n;
    const candidate = makeCandidate(deltaUnit);
    const candidateQuoteSummary = calculateCustomerQuoteSummary(candidate);
    const candidateMoneyValues = [
      ...candidate.lines.flatMap((line) => [
        line.costUnitPrice,
        line.sourceCostAmount,
        line.costAmount,
        line.quoteUnitPrice,
        line.quoteAmount,
      ]),
      candidateQuoteSummary.customerPaymentTotalAmount,
      candidateQuoteSummary.agentCollectionTotalAmount,
      candidateQuoteSummary.contractReceivableAmount,
      candidateQuoteSummary.internalCostTotalAmount,
      candidateQuoteSummary.expectedGrossProfitAmount,
    ];
    if (candidateMoneyValues.some((value) => (
      value !== null && !isCustomerQuoteMoneyInRange(moneyValue(value))
    ))) {
      if (deltaUnit < 0n) lowerDeltaUnit = deltaUnit + 1n;
      else if (deltaUnit > 0n) upperDeltaUnit = deltaUnit - 1n;
      else break;
      continue;
    }
    const candidateSummary = calculateCustomerQuoteRoleSummary(candidate);
    const candidateUnit = moneyValue(
      role === "student" ? candidateSummary.studentUnitPrice : candidateSummary.teacherUnitPrice,
    );
    if (candidateUnit === target) return candidate;
    if (candidateUnit < target) lowerDeltaUnit = deltaUnit + 1n;
    else upperDeltaUnit = deltaUnit - 1n;
  }
  throw new Error("目标角色报价单价在系统金额范围内无法精确实现");
}

/** 创建一条必须填写原因的报价专属零成本行。 */
export function createManualCustomerQuoteLine(lineNo: number): CustomerQuoteLine {
  return {
    lineOrigin: "manual",
    sourceBudgetLineId: null,
    lineNo,
    resourceType: "杂费",
    feeCategory: "misc",
    travelerRole: null,
    sourceQuantity: null,
    quantity: "1",
    unit: "项",
    costUnitPrice: "0",
    sourceCostAmount: null,
    costAmount: "0",
    quoteUnitPrice: "0.00",
    quoteAmount: "0.00",
    customerName: "新增报价项",
    customerDesc: "",
    profitPolicy: "allowed",
    displayMode: "merge",
    mergeTargetGroup: "其他",
    allocationMode: "fixed",
    isLinkedToPeopleCount: false,
    peopleMode: "manual",
    teacherPaymentPolicy: "not_applicable",
    financeTreatment: "contract_receivable",
    agentCollectionCategory: null,
    agentCollectionSuggested: false,
    isCostLocked: true,
    adjustmentKind: "other",
    adjustmentReason: "",
    remark: "",
  };
}

/** 更新报价行可编辑字段，并按数量和报价单价即时重算报价金额。 */
function quoteSourceRemainder(line: CustomerQuoteLine): bigint {
  const conversion = line.sourceEntrySnapshot?.conversion;
  if (!conversion || typeof conversion !== "object") return 0n;
  const saved = conversion as Record<string, unknown>;
  if (quantityValue(line.quantity) !== quantityValue(String(saved.quantity))
    || quantityValue(line.sourceQuantity ?? "0") !== quantityValue(line.quantity)
    || moneyValue(line.costUnitPrice) !== moneyValue(String(saved.unitPrice))
    || moneyValue(line.costAmount) !== moneyValue(String(saved.amount))) return 0n;
  return moneyValue(line.costAmount) - multiplyMoneyByQuantity(moneyValue(line.costUnitPrice), quantityValue(line.quantity));
}

/** 在完整核价成本上应用单价差，转换尾差不作为加价。 */
function quoteAmountWithSourceRemainder(line: CustomerQuoteLine): bigint {
  return multiplyMoneyByQuantity(moneyValue(line.quoteUnitPrice), quantityValue(line.quantity)) + quoteSourceRemainder(line);
}

/** 更新报价因素，并保持代收零加价及来源成本尾差。 */
export function patchCustomerQuoteLine(
  line: CustomerQuoteLine,
  patch: Partial<CustomerQuoteLine>,
): CustomerQuoteLine {
  const next = { ...line, ...patch };
  if (next.financeTreatment === "agent_collection") {
    return { ...next, quoteUnitPrice: next.costUnitPrice, quoteAmount: next.costAmount };
  }
  if (patch.quoteAmount !== undefined) {
    const nextAmount = moneyValue(patch.quoteAmount);
    const nextQuantity = quantityValue(next.quantity);
    const nextUnitPrice = unitPriceFromAmount(nextAmount - quoteSourceRemainder(next), nextQuantity);
    return {
      ...next,
      quoteUnitPrice: money(nextUnitPrice),
      quoteAmount: money(multiplyMoneyByQuantity(nextUnitPrice, nextQuantity) + quoteSourceRemainder(next)),
    };
  }
  return {
    ...next,
    quoteAmount: money(
      quoteAmountWithSourceRemainder(next),
    ),
  };
}

/** 读取费用项当前每单位加价，始终使用定点金额计算。 */
export function customerQuoteUnitMarkup(line: CustomerQuoteLine): string {
  return money(moneyValue(line.quoteUnitPrice) - moneyValue(line.costUnitPrice));
}

/** 按每单位加价更新报价单价，并复用报价行金额重算规则。 */
export function applyCustomerQuoteLineUnitMarkup(
  line: CustomerQuoteLine,
  markup: string,
): CustomerQuoteLine {
  if (line.financeTreatment === "agent_collection") throw new Error("代收代付不能加价");
  const normalizedMarkup = normalizeCustomerQuoteMoney(markup);
  if (normalizedMarkup === null) throw new Error("每单位加价金额不合法");
  if (quantityValue(line.quantity) === 0n) {
    throw new Error("数量为 0 的费用项不能调整每单位加价");
  }
  const nextUnitPriceValue = moneyValue(line.costUnitPrice) + moneyValue(normalizedMarkup);
  if (nextUnitPriceValue < 0n) throw new Error("报价单价不能为负数");
  const nextUnitPrice = money(nextUnitPriceValue);
  if (normalizeCustomerQuoteMoney(nextUnitPrice) === null) {
    throw new Error("报价单价超出金额范围");
  }
  const nextLine = patchCustomerQuoteLine(line, { quoteUnitPrice: nextUnitPrice });
  if (
    compareCustomerQuoteMoney(nextLine.quoteAmount) < 0
    || normalizeCustomerQuoteMoney(nextLine.quoteAmount) === null
  ) {
    throw new Error("报价金额超出合法范围");
  }
  return nextLine;
}

/** 对同名费用组的全部角色原子应用基础加价。 */
export function applyCustomerQuotePricingGroupMarkup(
  snapshot: CustomerQuoteSnapshot,
  lineNos: number[],
  markup: string,
): CustomerQuoteSnapshot {
  const normalizedMarkup = normalizeCustomerQuoteMoney(markup);
  if (normalizedMarkup === null) throw new Error("每单位加价金额不合法");
  const targets = new Set(lineNos);
  if (targets.size === 0) throw new Error("统一加价组不能为空");
  const targetLines = snapshot.lines.filter((line) => targets.has(line.lineNo));
  if (targetLines.length !== targets.size) throw new Error("统一加价组成员已变化，请刷新后重试");
  if (targetLines.some((line) => line.profitPolicy === "none" || line.financeTreatment === "agent_collection")) {
    throw new Error("该费用项不允许加价");
  }
  const baseMarkup = moneyValue(normalizedMarkup);
  const lines = snapshot.lines.map((line) => {
    if (!targets.has(line.lineNo)) return line;
    const role = (line.travelerRole ?? "").trim().toLowerCase();
    const factor = line.feeCategory === "ticket" && teacherRoles.has(role) ? 2n : 1n;
    const nextUnitPriceValue = moneyValue(line.costUnitPrice) + baseMarkup * factor;
    if (nextUnitPriceValue < 0n) throw new Error("报价单价不能为负数");
    const nextUnitPrice = money(nextUnitPriceValue);
    if (normalizeCustomerQuoteMoney(nextUnitPrice) === null) {
      throw new Error("报价单价超出金额范围");
    }
    const quoteAmount = money(
      multiplyMoneyByQuantity(nextUnitPriceValue, quantityValue(line.quantity)),
    );
    if (normalizeCustomerQuoteMoney(quoteAmount) === null) {
      throw new Error("报价金额超出合法范围");
    }
    return { ...line, quoteUnitPrice: nextUnitPrice, quoteAmount };
  });
  const stayMarkupChanged = targetLines.some((line) => {
    if (!isCustomerQuotePersonNightStay(snapshot, line)) return false;
    const nextLine = lines.find((candidate) => candidate.lineNo === line.lineNo);
    return nextLine?.quoteUnitPrice !== line.quoteUnitPrice || nextLine.quoteAmount !== line.quoteAmount;
  });
  if (!stayMarkupChanged || !snapshot.stayHighMarkupConfirmed) {
    return { ...snapshot, lines };
  }
  return { ...snapshot, lines, stayHighMarkupConfirmed: false };
}
